import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { getAddOns, editAddOns, addOns } from "store/actions/addOnsAction";

const AddOnsModal = ({
  isOpen,
  toggle,
  editMode = false,
  activeAddOns = null,
}) => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const [addOnsData, setAddOnsData] = useState({
    name: "",
    price: 0,
    description: "",
    image: null,
  });
  const [originalData, setOriginalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddOnsData({
      ...addOnsData,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/jpg",
      "image/svg",
      "image/webp",
    ];

    if (e.target.files.length) {
      const file = e.target.files[0];
      const fileType = file.type;

      if (allowedTypes.includes(fileType)) {
        setAddOnsData((prev) => ({
          ...prev,
          image: file,
        }));
      } else {
        toast.error(
          "Invalid file type. Please use a jpeg, png, gif, jpg, svg, or webp file."
        );
      }
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAddOnsData((prev) => ({ ...prev, image: null }));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleAddOns = () => {
    if (!addOnsData.image) {
      toast.warning("Please upload an image before submitting.");
      return;
    }
    const payload = {
      name: addOnsData?.name,
      description: addOnsData?.description,
      image: addOnsData?.image,
      price: addOnsData?.price || 0,
      restaurantId: user?.restaurantID,
    };
    setIsLoading(true);
    dispatch(
      addOns(payload, () => {
        setTimeout(() => {
          dispatch(getAddOns(user?.restaurantID, "", 5, 0));
          setIsLoading(false);
          setAddOnsData({
            name: "",
            description: "",
            price: 0,
            image: null,
          });
          toggle();
          toast.success("Add On Added Successfully");
        }, 3000);
      })
    );
  };

  const handleEditAddOns = () => {
    const updatedData = {
      name: addOnsData?.name,
      description: addOnsData?.description,
      image: addOnsData?.image,
      price: addOnsData?.price,
    };
    setIsLoading(true);
    dispatch(
      editAddOns(
        activeAddOns?.id,
        updatedData,
        () => {
          setTimeout(() => {
            dispatch(getAddOns(user?.restaurantID, "", 5, 0));
            setIsLoading(false);
            setAddOnsData({
              name: "",
              description: "",
              price: 0,
              image: null,
            });
            toggle();
            toast.success("Add On Updated Successfully");
          }, 3000);
        },
        () => {
          setIsLoading(false);
        }
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editMode) {
      handleEditAddOns();
    } else {
      handleAddOns();
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editMode && activeAddOns) {
        const data = {
          name: activeAddOns.name || "",
          description: activeAddOns.description || "",
          image: activeAddOns.image || null,
          price: activeAddOns.price || 0,
        };
        setAddOnsData(data);
        setOriginalData(data);
      } else {
        const empty = { name: "", description: "", price: 0, image: null };
        setAddOnsData(empty);
        setOriginalData(empty);
      }
    }
  }, [isOpen, editMode, activeAddOns]);

  const isSaveDisabled =
    isLoading ||
    !addOnsData?.name?.trim() ||
    (editMode && JSON.stringify(addOnsData) === JSON.stringify(originalData));

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle} className="align-items-center border-bottom">
        {editMode ? "Edit Add On" : "Add On"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="labelName">Add On Name</Label>
            <Input
              id="name"
              type="text"
              name="name"
              required
              placeholder="Enter Add On Name"
              value={addOnsData?.name}
              onChange={handleChange}
            />
          </FormGroup>
          <div className="d-flex flex-column justify-content-start align-items-start mb-3">
            <Label>Add On Image</Label>
            <label
              htmlFor="upload-button"
              className="form-file-input position-relative d-flex align-items-center justify-content-center p-4 w-100 rounded cursor-pointer"
            >
              {addOnsData?.image ? (
                <>
                  <img
                    src={
                      typeof addOnsData?.image === "string"
                        ? addOnsData?.image
                        : URL.createObjectURL(addOnsData?.image)
                    }
                    alt="addOns-img"
                    className="form-input-img"
                  />
                  <Button
                    size="sm"
                    color="danger"
                    className="position-absolute top-2 right-2"
                    onClick={handleRemoveImage}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <span className="fa-stack fa-2x">
                  <i className="fas fa-circle fa-stack-2x" />
                  <i className="fas fa-store fa-stack-1x fa-inverse" />
                </span>
              )}
            </label>
            <input
              id="upload-button"
              type="file"
              ref={inputRef}
              className="d-none"
              onChange={handleImageChange}
            />
          </div>
          <FormGroup>
            <Label for="labelPrice">Price</Label>
            <Input
              id="price"
              type="number"
              name="price"
              placeholder="Enter Add On Price"
              value={addOnsData?.price}
              onChange={(e) =>
                setAddOnsData((prev) => ({
                  ...prev,
                  price: e.target.value === "" ? "" : Number(e.target.value),
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label for="description">Description</Label>
            <Input
              id="description"
              type="textarea"
              resize="none"
              rows= {5}
              name="description"
              placeholder="Enter Add ons Description"
              value={addOnsData?.description}
              onChange={(e) =>
                setAddOnsData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-top">
          <Button color="primary" type="submit" disabled={isSaveDisabled}>
            {isLoading && <Spinner size="sm" className="mr-2" />}
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddOnsModal;
